// Glosario en contexto (ficha del lote) — un ícono "?" chico junto a cada
// término técnico que, al pasar el mouse (desktop) o tocar/focus (mobile/kbd),
// muestra una explicación corta en criollo.
//
// - Solo UI. NO cambia datos, cálculos ni la lógica de la ficha.
// - Regla de honestidad (proyecto): la copy de "Valuación oficial" aclara que
//   es FISCAL, no de mercado. La de "m² construibles" menciona el tope FOT.
// - Diseño: ícono `HelpCircle` (lucide) 12px, tenue (slate-400 → emerald-700).
//   Tooltip fondo blanco sólido, texto slate-700 (contraste alto, legible).
// - Interacción:
//     · Desktop (mouse): hover abre, mouse-leave cierra (native Radix Tooltip).
//     · Mobile (touch): tap hace focus → Radix Tooltip se abre en focus.
//       Tap fuera → blur → cierra.
//     · Teclado: Tab enfoca → abre; Tab out → cierra (accesibilidad nativa).
// - Radix Tooltip evita el problema de estado compartido y sincronización
//   pointerenter/leave que teníamos con Popover manual.
import React from "react";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function TermHelp({ text, label, testid }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Ayuda: ${label}`}
            data-testid={testid}
            // onClick para que en mobile el TAP dispare el focus explícito
            // (algunos browsers no enfocan un <button> con tap sin click activo).
            onClick={(e) => e.currentTarget.focus()}
            className="inline-flex items-center align-middle text-slate-400 hover:text-emerald-700 focus:text-emerald-700 focus:outline-none transition-colors ml-1"
          >
            <HelpCircle className="w-3 h-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={4}
          className="w-64 p-3 text-xs leading-snug bg-white text-slate-700 border border-slate-300 shadow-lg z-[600] whitespace-normal"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Textos de los términos del glosario (exactos del brief).
// Regla de honestidad: dos copies clave —
//   · valuacion  → "referencia oficial, NO precio de mercado"
//   · m2_constr  → "tope limitado por el FOT"
export const TERM_GLOSSARY = {
  fos: "Qué porcentaje del terreno podés ocupar con la construcción en planta baja. Ej.: FOS 70% = podés construir sobre el 70% del lote.",
  fot: "Cuántas veces la superficie del terreno podés construir en total, sumando todos los pisos. Ej.: FOT 2,5 en un lote de 1.000 m² = hasta 2.500 m² construibles.",
  m2_constr:
    "El máximo que podés construir en total, ya calculado y limitado por el FOT (la cuenta la hace el sistema por vos).",
  valuacion:
    "El valor fiscal que le asigna el catastro de la Provincia (IDECOR). Es una referencia oficial — NO es el precio de mercado.",
  ars_m2:
    "La valuación oficial dividida por la superficie: cuánto vale el m² según el catastro.",
  retiros:
    "Cuánto tenés que separar la construcción de la línea de la calle o de los límites del lote, según la normativa.",
  sup_min:
    "El tamaño mínimo que exige la normativa para poder construir/subdividir en esa zona.",
  frente_min:
    "El frente mínimo que exige la normativa para poder construir/subdividir en esa zona.",
  categoria_uso:
    "Para qué está zonificado el lote (residencial, equipamientos, etc.).",
  caracter_urb:
    "La orientación de la zona según la ordenanza (ej.: renovación, densificación).",
  baldio:
    "Sin mejoras declaradas en el catastro. OJO: puede haber construcción no declarada → verificá visualmente (Street View) antes de contactar.",
  proteccion:
    "El lote tiene condición patrimonial/histórica → verificá la ordenanza antes de proyectar.",
};
